export type ContentBlock = {
  type: 'heading' | 'heading3' | 'paragraph' | 'list' | 'callout' | 'formula'
  content?: string
  items?: string[]
}

export type SEOArticle = {
  slug: string
  title: string
  cluster: string
  metaDescription: string
  content: ContentBlock[]
  relatedArticles: string[]
}

export const articles: SEOArticle[] = [
  // --- CLUSTER 1: SEO Fundamentals ---
  {
    slug: 'wat-is-seo-en-hoe-werkt-het',
    title: 'Wat is SEO en hoe werkt het?',
    cluster: 'SEO Fundamentals',
    metaDescription:
      'Een heldere, niet-technische uitleg over wat SEO is, hoe zoekmachines werken en waarom het essentieel is voor structurele online groei.',
    content: [
      { type: 'heading', content: 'SEO is geen magie, het is een systeem' },
      {
        type: 'paragraph',
        content:
          'Veel ondernemers zien SEO (Search Engine Optimization) als een soort zwarte magie of een eenmalig trucje dat je toepast op je website. De realiteit is veel logischer. SEO is simpelweg het proces waarbij je jouw website zo inricht dat zoekmachines (zoals Google) precies begrijpen wat je doet, voor wie je het doet, en waarom jij de beste keuze bent.',
      },
      {
        type: 'callout',
        content:
          'Google heeft maar één doel: de zoeker zo snel mogelijk het allerbeste antwoord geven op zijn of haar vraag.',
      },
      { type: 'heading3', content: 'Hoe werkt Google eigenlijk?' },
      {
        type: 'paragraph',
        content:
          'Om te snappen hoe SEO werkt, moet je snappen hoe Google werkt. Het proces bestaat uit drie stappen:',
      },
      {
        type: 'list',
        items: [
          'Crawling: Google stuurt "bots" (kleine stukjes software) het internet op om nieuwe en vernieuwde pagina\'s te ontdekken.',
          "Indexing: De gevonden pagina's worden geanalyseerd en opgeslagen in een gigantische database (de index).",
          'Ranking: Wanneer iemand een zoekopdracht intypt, zoekt Google in de index naar de meest relevante en betrouwbare resultaten en toont deze in een specifieke volgorde.',
        ],
      },
      { type: 'heading3', content: 'De 4 pijlers van SEO' },
      {
        type: 'paragraph',
        content:
          'Succesvolle SEO rust altijd op vier pijlers. Als één van deze pijlers ontbreekt, stort het systeem in.',
      },
      {
        type: 'list',
        items: [
          'Techniek: Kan Google je website überhaupt vinden, lezen en begrijpen? Is de site snel en veilig?',
          'Content: Heb je de antwoorden op de vragen die jouw doelgroep stelt? Is de inhoud diepgaand en relevant?',
          'Autoriteit: Vertrouwen andere websites jou? (Dit meten we vaak in backlinks).',
          'Gebruikerservaring (UX): Vinden bezoekers makkelijk hun weg, of klikken ze direct weer weg?',
        ],
      },
      {
        type: 'formula',
        content: 'Zichtbaarheid = Techniek × Content × Autoriteit × UX',
      },
      {
        type: 'paragraph',
        content:
          'Als één van deze factoren een 0 is, is de uitkomst 0. Je kunt de beste content ter wereld hebben, maar als je website technisch onleesbaar is voor Google, rank je niet.',
      },
    ],
    relatedArticles: [
      'waarom-werkt-seo-vaak-niet',
      'hoe-lang-duurt-seo',
      'seo-vs-advertenties',
    ],
  },
  {
    slug: 'waarom-werkt-seo-vaak-niet',
    title: 'Waarom werkt SEO vaak niet?',
    cluster: 'SEO Fundamentals',
    metaDescription:
      'Heb je geïnvesteerd in SEO maar zie je geen resultaat? Ontdek de meest gemaakte fouten en waarom losse trucjes niet meer werken.',
    content: [
      { type: 'heading', content: 'Waarom je SEO-investering niets oplevert' },
      {
        type: 'paragraph',
        content:
          'Het is een veelgehoorde klacht: "We hebben een SEO-bureau ingehuurd, ze hebben wat teksten geschreven, maar we zien geen resultaat." SEO faalt zelden omdat het kanaal niet werkt. Het faalt omdat de aanpak verkeerd is.',
      },
      {
        type: 'heading3',
        content: '1. Losse flodders in plaats van een systeem',
      },
      {
        type: 'paragraph',
        content:
          'De grootste fout is SEO zien als een losse dienst. Een blogje hier, een zoekwoordje daar. Zonder een overkoepelende structuur (zoals een SEO Silo) begrijpt Google de context van je website niet. Je bouwt een huis zonder fundering.',
      },
      { type: 'heading3', content: '2. Verkeerde zoekintentie' },
      {
        type: 'paragraph',
        content:
          'Je kunt perfect geoptimaliseerd zijn voor een zoekwoord, maar als jouw pagina niet aansluit bij wat de zoeker eigenlijk wil (de zoekintentie), zul je nooit hoog ranken. Als iemand zoekt op "wat kost een accountant", wil hij een prijsindicatie, geen verkooppraatje over hoe goed jouw kantoor is.',
      },
      {
        type: 'callout',
        content:
          'Stop met focussen op zoekwoorden. Focus op de vragen en problemen van je klant.',
      },
      { type: 'heading3', content: '3. Geen geduld' },
      {
        type: 'paragraph',
        content:
          'SEO is een vliegwiel. Het kost tijd om het op gang te brengen. Veel bedrijven stoppen na 3 maanden omdat ze nog niet op nummer 1 staan, precies op het moment dat de resultaten zouden beginnen te komen.',
      },
    ],
    relatedArticles: ['wat-is-seo-en-hoe-werkt-het', 'zoekintentie-uitleg'],
  },
  {
    slug: 'hoe-lang-duurt-seo',
    title: 'Hoe lang duurt SEO voordat je resultaat ziet?',
    cluster: 'SEO Fundamentals',
    metaDescription:
      'De eerlijke waarheid over hoe lang SEO duurt. Wat kun je verwachten in maand 1, maand 3 en na een jaar?',
    content: [
      { type: 'heading', content: 'De eerlijke tijdlijn van SEO' },
      {
        type: 'paragraph',
        content:
          'De meest gestelde vraag in SEO is: "Wanneer sta ik op nummer 1?" Het eerlijke antwoord is dat SEO geen sprint is, maar een marathon. Het is een investering in lange termijn zichtbaarheid.',
      },
      { type: 'heading3', content: 'Maand 1-2: De Fundering' },
      {
        type: 'paragraph',
        content:
          'In de eerste maanden zie je vaak nog geen stijging in verkeer. Dit is de fase waarin technische problemen worden opgelost, de sitestructuur wordt verbeterd en de eerste content wordt gecreëerd. Google moet deze wijzigingen eerst ontdekken en verwerken.',
      },
      { type: 'heading3', content: 'Maand 3-6: De Eerste Tractie' },
      {
        type: 'paragraph',
        content:
          "Je begint te ranken op long-tail zoekwoorden (specifieke, langere zoekopdrachten). Het verkeer begint langzaam te stijgen. Je ziet dat pagina's klimmen van pagina 5 naar pagina 2 in Google.",
      },
      { type: 'heading3', content: 'Maand 6-12: Het Vliegwiel' },
      {
        type: 'paragraph',
        content:
          'Nu begint het systeem echt te werken. De opgebouwde autoriteit en de onderlinge links tussen je content (de silo-structuur) versterken elkaar. Je begint te ranken op competitievere zoekwoorden en de leads komen consistenter binnen.',
      },
      {
        type: 'callout',
        content:
          'SEO is als rente op rente. De inspanningen van vandaag leveren over een jaar exponentieel meer op.',
      },
    ],
    relatedArticles: ['seo-vs-advertenties', 'wat-is-seo-en-hoe-werkt-het'],
  },
  {
    slug: 'seo-vs-advertenties',
    title: 'SEO vs advertenties: wat is het verschil?',
    cluster: 'SEO Fundamentals',
    metaDescription:
      'Kies je voor SEO of Google Ads? Ontdek de verschillen, voor- en nadelen, en wanneer je welke strategie moet inzetten.',
    content: [
      { type: 'heading', content: 'Kopen of bouwen?' },
      {
        type: 'paragraph',
        content:
          'Zowel SEO (organisch) als SEA (adverteren, zoals Google Ads) hebben als doel om bovenaan in Google te komen. Maar de manier waarop, en de lange termijn effecten, zijn compleet verschillend.',
      },
      {
        type: 'heading3',
        content: 'Google Ads (SEA): Huren van zichtbaarheid',
      },
      {
        type: 'paragraph',
        content:
          'Met advertenties betaal je per klik (PPC). Het grote voordeel is snelheid: je stelt een campagne in en je staat vandaag nog bovenaan. Het grote nadeel? Zodra je stopt met betalen, ben je direct onzichtbaar. Je bouwt geen blijvende waarde op.',
      },
      { type: 'heading3', content: 'SEO: Bouwen van digitaal vastgoed' },
      {
        type: 'paragraph',
        content:
          'SEO kost tijd en inspanning vooraf. Maar de content die je vandaag maakt, kan over drie jaar nog steeds dagelijks gratis bezoekers opleveren. Je bouwt een asset op die in waarde stijgt.',
      },
      {
        type: 'callout',
        content:
          'De beste strategie is vaak een combinatie: gebruik Ads voor directe leads en test data, terwijl je op de achtergrond bouwt aan je SEO fundering.',
      },
    ],
    relatedArticles: ['wat-is-seo-en-hoe-werkt-het', 'hoe-lang-duurt-seo'],
  },

  // --- CLUSTER 2: SEO Silo (Core) ---
  {
    slug: 'seo-silo-wat-is-het',
    title: 'Wat is een SEO silo en hoe werkt het?',
    cluster: 'SEO Silo (Core)',
    metaDescription:
      'Een SEO silo is de krachtigste manier om je website te structureren. Ontdek hoe je met topic clusters dominant wordt in Google.',
    content: [
      { type: 'heading', content: 'Waarom 90% van de websites onzichtbaar is' },
      {
        type: 'paragraph',
        content:
          "De meeste websites zijn een platte verzameling van pagina's. Een homepagina, een dienstenpagina, en een blog waar willekeurige artikelen op een hoop worden gegooid. Voor Google is dit een chaos. Er is geen hiërarchie, geen context en geen duidelijke expertise.",
      },
      {
        type: 'paragraph',
        content:
          'De oplossing? De SEO Silo structuur (ook wel Topic Clusters genoemd).',
      },
      { type: 'heading3', content: 'Wat is een SEO Silo?' },
      {
        type: 'paragraph',
        content:
          'Een SEO silo is een manier om de content op je website thematisch te groeperen. Je bouwt als het ware "kennis-eilanden" rondom specifieke hoofdonderwerpen. In plaats van dat alle pagina\'s losjes aan elkaar hangen, creëer je een strakke, logische hiërarchie.',
      },
      {
        type: 'list',
        items: [
          'Pillar Page (De Hoofdpagina): Een uitgebreide pagina over het hoofdonderwerp (bijv. "Hypotheek oversluiten").',
          'Cluster Content (De Verdieping): Specifieke artikelen die dieper ingaan op sub-onderwerpen (bijv. "Kosten hypotheek oversluiten", "Wanneer hypotheek oversluiten zinvol is").',
          'Interne Links: De cluster artikelen linken allemaal terug naar de Pillar Page, en vice versa.',
        ],
      },
      { type: 'heading3', content: 'Waarom werkt dit zo goed?' },
      {
        type: 'paragraph',
        content:
          'Google wil experts tonen. Als jij 15 goed gestructureerde, onderling gelinkte artikelen hebt over "bedrijfsovername", ziet Google jou als een absolute autoriteit op dat gebied. Veel meer dan een concurrent die er slechts één algemene pagina over heeft.',
      },
      {
        type: 'callout',
        content:
          'Een silo vertelt Google: "Kijk, we hebben niet zomaar één pagina over dit onderwerp, we hebben de hele context gedekt."',
      },
      {
        type: 'formula',
        content:
          'Dominantie = Silo Structuur × Content Kwaliteit × Interne Links',
      },
      { type: 'heading3', content: 'Het resultaat van een goede silo' },
      {
        type: 'paragraph',
        content:
          'Wanneer je een silo goed opzet, gebeurt er iets magisch: als één artikel in de silo stijgt in Google, trekt het de rest van de silo mee omhoog via de interne links. De autoriteit stroomt door de hele structuur heen.',
      },
    ],
    relatedArticles: [
      'seo-silo-opzetten',
      'waarom-interne-linking-cruciaal-is',
      'hoe-google-websites-begrijpt',
    ],
  },
  {
    slug: 'seo-silo-opzetten',
    title: 'Hoe bouw je een SEO silo structuur?',
    cluster: 'SEO Silo (Core)',
    metaDescription:
      'Een praktische, stap-voor-stap gids voor het opzetten van een ijzersterke SEO silo structuur voor jouw website.',
    content: [
      { type: 'heading', content: 'Van theorie naar praktijk: je silo bouwen' },
      {
        type: 'paragraph',
        content:
          'Je weet nu wat een SEO silo is en waarom het werkt. Maar hoe pas je dit toe op jouw eigen website? Het bouwen van een silo vereist planning vooraf. Je kunt niet zomaar beginnen met schrijven.',
      },
      { type: 'heading3', content: "Stap 1: Bepaal je kernthema's (Pillars)" },
      {
        type: 'paragraph',
        content:
          'Kies 3 tot 5 hoofdonderwerpen waar jouw bedrijf op gevonden wil worden. Voor een financieel adviseur kunnen dit bijvoorbeeld zijn: Hypotheken, Pensioenen, en Vermogensopbouw. Dit worden je Pillar Pages.',
      },
      {
        type: 'list',
        items: [
          'Kies onderwerpen met voldoende zoekvolume.',
          'Zorg dat het onderwerpen zijn waar je veel over kunt vertellen (minimaal 5-10 sub-onderwerpen).',
          'Dit zijn vaak je belangrijkste commerciële diensten.',
        ],
      },
      {
        type: 'heading3',
        content: 'Stap 2: Zoekwoordonderzoek voor de clusters',
      },
      {
        type: 'paragraph',
        content:
          'Voor elk kernthema ga je op zoek naar de specifieke vragen die je doelgroep stelt. Gebruik tools zoals Ahrefs, SEMrush of AnswerThePublic. Zoek naar long-tail zoekwoorden. Bijvoorbeeld rondom "Pensioenen": "pensioen opbouwen als zzp\'er", "lijfrentepolis uitkeren", "pensioengat berekenen".',
      },
      { type: 'heading3', content: 'Stap 3: De URL-structuur inrichten' },
      {
        type: 'paragraph',
        content:
          'Een goede silo is vaak ook zichtbaar in de URL-structuur van je website. Dit helpt Google de hiërarchie te begrijpen.',
      },
      {
        type: 'list',
        items: [
          'Pillar: jouwsite.nl/pensioen/',
          'Cluster 1: jouwsite.nl/pensioen/zzp-pensioen-opbouwen/',
          'Cluster 2: jouwsite.nl/pensioen/pensioengat-berekenen/',
        ],
      },
      {
        type: 'callout',
        content:
          'Let op: Een fysieke URL-structuur (met mappen) is sterk, maar een virtuele silo (puur gebaseerd op interne links) kan net zo effectief zijn als je CMS geen mappen toelaat.',
      },
      {
        type: 'heading3',
        content: 'Stap 4: Content creatie en Interne Linking',
      },
      {
        type: 'paragraph',
        content:
          'Schrijf de content. De Pillar Page is een breed overzicht (2000+ woorden). De cluster artikelen gaan diep in op één specifiek detail (800-1200 woorden). De gouden regel: elk cluster artikel linkt terug naar de Pillar Page met een exact zoekwoord als ankertekst, en de Pillar Page linkt naar alle cluster artikelen.',
      },
    ],
    relatedArticles: [
      'seo-silo-wat-is-het',
      'waarom-interne-linking-cruciaal-is',
      'veelgemaakte-fouten-website-structuur',
    ],
  },
  {
    slug: 'waarom-interne-linking-cruciaal-is',
    title: 'Waarom interne linking cruciaal is',
    cluster: 'SEO Silo (Core)',
    metaDescription:
      'Interne links zijn de snelwegen van je website. Ontdek hoe je met de juiste linkstrategie je rankings drastisch kunt verbeteren.',
    content: [
      { type: 'heading', content: 'De verborgen kracht van SEO' },
      {
        type: 'paragraph',
        content:
          'Iedereen praat over backlinks (links van andere websites naar jouw site). Maar de meest onderschatte en krachtigste SEO-tactiek heb je zelf 100% in de hand: interne linking.',
      },
      { type: 'heading3', content: 'Wat doen interne links?' },
      {
        type: 'paragraph',
        content: 'Interne links doen drie cruciale dingen voor je website:',
      },
      {
        type: 'list',
        items: [
          'Ze verdelen "Link Juice" (autoriteit) over je website. Een sterke pagina kan zwakkere pagina\'s omhoog trekken.',
          'Ze helpen Google de structuur en hiërarchie van je site te begrijpen.',
          'Ze houden bezoekers langer op je website door ze naar relevante vervolginformatie te sturen.',
        ],
      },
      {
        type: 'callout',
        content:
          'Een pagina zonder interne links is een "orphan page" (weespagina). Google kan deze moeilijk vinden en geeft er vrijwel geen waarde aan.',
      },
    ],
    relatedArticles: ['seo-silo-wat-is-het', 'seo-silo-opzetten'],
  },
  {
    slug: 'veelgemaakte-fouten-website-structuur',
    title: 'Veelgemaakte fouten in website structuur',
    cluster: 'SEO Silo (Core)',
    metaDescription:
      'Voorkom deze veelgemaakte fouten in je website structuur die je SEO rankings saboteren.',
    content: [
      { type: 'heading', content: 'Waarom Google verdwaalt op jouw site' },
      {
        type: 'paragraph',
        content:
          'Een slechte website structuur is als een supermarkt zonder bordjes. Zowel je klanten als Google raken de weg kwijt. Dit zijn de grootste fouten.',
      },
      { type: 'heading3', content: '1. De platte structuur' },
      {
        type: 'paragraph',
        content:
          "Alle pagina's hangen direct onder de homepagina. Er is geen categorisatie. Dit maakt het onmogelijk voor Google om te bepalen welke pagina's het belangrijkst zijn.",
      },
      { type: 'heading3', content: '2. Te diepe navigatie' },
      {
        type: 'paragraph',
        content:
          'Als een bezoeker (of Google bot) meer dan 3 of 4 keer moet klikken vanaf de homepagina om een artikel te bereiken, is de pagina te diep begraven. Dit kost autoriteit.',
      },
    ],
    relatedArticles: ['seo-silo-wat-is-het', 'hoe-google-websites-begrijpt'],
  },
  {
    slug: 'hoe-google-websites-begrijpt',
    title: 'Hoe Google websites begrijpt (simpel uitgelegd)',
    cluster: 'SEO Silo (Core)',
    metaDescription:
      'Kijk door de ogen van een zoekmachine. Hoe leest, begrijpt en waardeert Google de content op jouw website?',
    content: [
      { type: 'heading', content: 'Kijken door de ogen van een bot' },
      {
        type: 'paragraph',
        content:
          'Google is slim, maar het is geen mens. Het leest geen tekst zoals wij dat doen, het analyseert code, structuur en relaties tussen woorden (entiteiten).',
      },
      { type: 'heading3', content: 'Context is koning' },
      {
        type: 'paragraph',
        content:
          'Vroeger keek Google puur naar hoe vaak een zoekwoord op een pagina stond. Tegenwoordig kijkt Google naar de context. Als je schrijft over "Apple", weet Google door de omringende woorden (iPhone, Mac, of juist fruit, boom) precies wat je bedoelt.',
      },
    ],
    relatedArticles: ['seo-silo-wat-is-het', 'wat-is-entity-seo'],
  },

  // --- CLUSTER 3: Technical SEO ---
  {
    slug: 'technische-seo-uitleg',
    title: 'Wat is technische SEO?',
    cluster: 'Technical SEO',
    metaDescription:
      'Technische SEO is de fundering van je website. Zonder een goede technische basis is al je content en linkbuilding zinloos. Ontdek de basisprincipes.',
    content: [
      { type: 'heading', content: 'De onzichtbare fundering van je website' },
      {
        type: 'paragraph',
        content:
          'Stel je voor dat je een prachtig huis bouwt, met de mooiste meubels en de beste inrichting. Maar je vergeet de fundering te storten en de deuren klemmen. Niemand kan naar binnen, en bij de eerste storm stort het in. Dat is precies wat er gebeurt als je investeert in content zonder technische SEO.',
      },
      {
        type: 'paragraph',
        content:
          'Technische SEO omvat alle optimalisaties aan de achterkant van je website die ervoor zorgen dat zoekmachines je site probleemloos kunnen crawlen (ontdekken) en indexeren (opslaan).',
      },
      { type: 'heading3', content: 'De drie pijlers van technische SEO' },
      {
        type: 'list',
        items: [
          "Crawlbaarheid: Kan de Googlebot alle belangrijke pagina's op je site vinden en bereiken? Dode links (404 errors) en oneindige redirects blokkeren de weg.",
          "Indexeerbaarheid: Mág Google de gevonden pagina's opslaan? Verkeerde instellingen in je robots.txt of noindex-tags kunnen je site onzichtbaar maken.",
          'Prestaties (Performance): Laadt de website snel? Is hij veilig (HTTPS)? Werkt hij perfect op mobiele telefoons?',
        ],
      },
      {
        type: 'callout',
        content:
          'Technische SEO is zelden de reden dat je op nummer 1 staat, maar het is heel vaak de reden dat je niét rankt.',
      },
      { type: 'heading3', content: 'Snelheid is een ranking factor' },
      {
        type: 'paragraph',
        content:
          'Google heeft weinig geduld, en jouw bezoekers al helemaal niet. Sinds de introductie van de Core Web Vitals is de laadsnelheid en de stabiliteit van je website een officiële, meetbare ranking factor geworden. Een trage website kost je direct posities in de zoekresultaten.',
      },
      { type: 'heading3', content: 'Mobiel eerst (Mobile-First Indexing)' },
      {
        type: 'paragraph',
        content:
          'Google kijkt tegenwoordig primair naar de mobiele versie van je website om te bepalen hoe hoog je rankt. Zelfs als 80% van jouw B2B-klanten via desktop komt, beoordeelt Google je op je mobiele weergave. Als je mobiele site elementen mist die wel op desktop staan, negeert Google die elementen.',
      },
    ],
    relatedArticles: [
      'wat-zijn-core-web-vitals',
      'hoe-werkt-indexatie',
      'wat-doet-een-xml-sitemap',
    ],
  },
  {
    slug: 'wat-zijn-core-web-vitals',
    title: 'Wat zijn Core Web Vitals?',
    cluster: 'Technical SEO',
    metaDescription:
      'Core Web Vitals zijn de snelheids- en gebruikerservaring-statistieken van Google. Wat betekenen LCP, FID en CLS voor jouw website?',
    content: [
      { type: 'heading', content: 'De snelheidsmeter van Google' },
      {
        type: 'paragraph',
        content:
          'Core Web Vitals zijn drie specifieke statistieken die Google gebruikt om de gebruikerservaring van een webpagina te meten. Ze focussen op laadsnelheid, interactiviteit en visuele stabiliteit.',
      },
      {
        type: 'list',
        items: [
          'LCP (Largest Contentful Paint): Hoe lang duurt het voordat het grootste element op het scherm geladen is? (Moet onder 2.5 seconden zijn).',
          'FID / INP (Interaction to Next Paint): Hoe snel reageert de pagina als je ergens op klikt?',
          'CLS (Cumulative Layout Shift): Verspringt de pagina nog tijdens het laden? (Denk aan knoppen die ineens verschuiven als je wilt klikken).',
        ],
      },
    ],
    relatedArticles: [
      'technische-seo-uitleg',
      'wat-is-ux-en-waarom-beinvloedt-het-seo',
    ],
  },
  {
    slug: 'hoe-werkt-indexatie',
    title: 'Hoe werkt indexatie in Google?',
    cluster: 'Technical SEO',
    metaDescription:
      'Hoe zorgt Google ervoor dat jouw pagina in de zoekresultaten verschijnt? Het proces van crawling en indexatie simpel uitgelegd.',
    content: [
      { type: 'heading', content: 'Van publicatie tot zoekresultaat' },
      {
        type: 'paragraph',
        content:
          'Je hebt op "Publiceren" geklikt. En nu? Google moet je pagina eerst vinden (crawlen), begrijpen, en opslaan in hun gigantische database (indexeren).',
      },
      {
        type: 'callout',
        content:
          'Als een pagina niet is geïndexeerd, bestaat hij simpelweg niet voor Google. Je kunt er onmogelijk mee ranken.',
      },
    ],
    relatedArticles: ['technische-seo-uitleg', 'wat-doet-een-xml-sitemap'],
  },
  {
    slug: 'wat-doet-een-xml-sitemap',
    title: 'Wat doet een XML sitemap?',
    cluster: 'Technical SEO',
    metaDescription:
      'Een XML sitemap is de plattegrond van je website voor zoekmachines. Waarom heb je er een nodig en hoe stel je hem in?',
    content: [
      { type: 'heading', content: 'De plattegrond voor Googlebot' },
      {
        type: 'paragraph',
        content:
          "Een XML sitemap is een speciaal bestand (niet bedoeld voor mensen) dat een lijst bevat van alle belangrijke pagina's op je website. Het vertelt Google precies welke pagina's er zijn, wanneer ze voor het laatst zijn geüpdatet, en hoe belangrijk ze zijn ten opzichte van elkaar.",
      },
    ],
    relatedArticles: ['technische-seo-uitleg', 'hoe-werkt-indexatie'],
  },
  {
    slug: 'wat-zijn-canonicals',
    title: 'Wat zijn canonicals en waarom zijn ze belangrijk?',
    cluster: 'Technical SEO',
    metaDescription:
      'Voorkom duplicate content problemen met canonical tags. Wat zijn het en hoe gebruik je ze correct?',
    content: [
      { type: 'heading', content: 'Het oplossen van dubbele content' },
      {
        type: 'paragraph',
        content:
          'Soms is dezelfde content bereikbaar via meerdere URL\'s. Dit verwart Google: welke versie moeten ze ranken? Een Canonical tag (rel="canonical") is een stukje code dat tegen Google zegt: "Dit is de originele, belangrijkste versie van deze pagina. Negeer de rest."',
      },
    ],
    relatedArticles: ['technische-seo-uitleg', 'voorkom-duplicate-content'],
  },

  // --- CLUSTER 4: Content SEO ---
  {
    slug: 'hoe-schrijf-je-seo-teksten',
    title: 'Hoe schrijf je SEO teksten?',
    cluster: 'Content SEO',
    metaDescription:
      'SEO teksten schrijven betekent niet meer zoekwoorden proppen. Leer hoe je content schrijft die zowel Google als je lezers geweldig vinden.',
    content: [
      {
        type: 'heading',
        content: 'Schrijf voor mensen, optimaliseer voor machines',
      },
      {
        type: 'paragraph',
        content:
          'De tijd dat je een zoekwoord 20 keer in een tekst moest proppen om te ranken, is gelukkig lang voorbij. Moderne SEO copywriting draait om het geven van het beste, meest complete antwoord op de vraag van de zoeker, verpakt in een prettig leesbaar format.',
      },
    ],
    relatedArticles: ['zoekintentie-uitleg', 'hoe-kies-je-keywords'],
  },
  {
    slug: 'zoekintentie-uitleg',
    title: 'Wat is zoekintentie en waarom is het zo belangrijk?',
    cluster: 'Content SEO',
    metaDescription:
      'Zoekintentie is de heilige graal van moderne SEO. Als je niet begrijpt wát de zoeker eigenlijk wil, zul je nooit ranken. Een complete uitleg.',
    content: [
      { type: 'heading', content: 'De "Waarom" achter de zoekopdracht' },
      {
        type: 'paragraph',
        content:
          'Zoekintentie (Search Intent) is de belangrijkste factor in moderne SEO. Het is het antwoord op de vraag: Waarom typt iemand deze specifieke woorden in Google in? Wat is het werkelijke doel van de zoeker?',
      },
      {
        type: 'paragraph',
        content:
          'Google is extreem goed geworden in het begrijpen van deze intentie. Als jij een pagina maakt die niet aansluit bij wat de zoeker wil, zal Google je pagina niet tonen, ongeacht hoe goed je technische SEO of linkbuilding is.',
      },
      { type: 'heading3', content: 'De 4 soorten zoekintentie' },
      {
        type: 'list',
        items: [
          'Informatief (Weten): De zoeker zoekt informatie of een antwoord op een vraag. Voorbeeld: "hoe bereken ik mijn pensioengat". Ze willen een blog of gids, geen verkooppagina.',
          'Navigerend (Gaan): De zoeker wil naar een specifieke website. Voorbeeld: "inloggen rabobank".',
          'Commercieel onderzoek (Vergelijken): De zoeker wil iets kopen, maar is nog aan het oriënteren. Voorbeeld: "beste boekhoudprogramma zzp". Ze willen reviews en vergelijkingen.',
          'Transactioneel (Kopen/Doen): De zoeker is klaar om actie te ondernemen. Voorbeeld: "boekhouder inhuren amsterdam". Ze willen een dienstenpagina met een duidelijke contactknop.',
        ],
      },
      {
        type: 'callout',
        content:
          'De grootste SEO-fout is proberen te ranken met een transactionele pagina (je dienstenpagina) op een informatieve zoekopdracht.',
      },
      { type: 'heading3', content: 'Hoe bepaal je de zoekintentie?' },
      {
        type: 'paragraph',
        content:
          "Het is eigenlijk heel simpel: vraag het aan Google. Typ je gewenste zoekwoord in en kijk naar de top 10 resultaten. Wat zie je? Zijn het blogs? Dan is de intentie informatief. Zijn het productpagina's? Dan is het transactioneel. Je moet het format van de top 10 volgen om kans te maken.",
      },
      {
        type: 'formula',
        content:
          'Ranking = Relevantie (Zoekintentie) × Autoriteit × Vertrouwen',
      },
    ],
    relatedArticles: [
      'hoe-schrijf-je-seo-teksten',
      'blogs-vs-landingspaginas',
      'hoe-kies-je-keywords',
    ],
  },
  {
    slug: 'hoe-kies-je-keywords',
    title: 'Hoe kies je de juiste keywords?',
    cluster: 'Content SEO',
    metaDescription:
      'Zoekwoordonderzoek is de basis van je content strategie. Leer hoe je zoekwoorden kiest die daadwerkelijk klanten opleveren.',
    content: [
      {
        type: 'heading',
        content: 'Zoekvolume is ijdelheid, intentie is omzet',
      },
      {
        type: 'paragraph',
        content:
          'Kies niet blind voor zoekwoorden met het hoogste zoekvolume. Een zoekwoord met 50 zoekopdrachten per maand van mensen die direct jouw dienst willen kopen, is veel waardevoller dan een zoekwoord met 5000 zoekopdrachten van studenten die informatie zoeken voor een werkstuk.',
      },
    ],
    relatedArticles: ['zoekintentie-uitleg', 'hoe-schrijf-je-seo-teksten'],
  },
  {
    slug: 'blogs-vs-landingspaginas',
    title: 'Wat is het verschil tussen blogs en landingspagina’s?',
    cluster: 'Content SEO',
    metaDescription:
      'Wanneer schrijf je een blog en wanneer maak je een landingspagina? Ontdek het verschil en hoe ze samenwerken in je SEO strategie.',
    content: [
      { type: 'heading', content: 'Informatie vs. Conversie' },
      {
        type: 'paragraph',
        content:
          "Blogs vangen informatieve zoekintentie af (mensen die vragen hebben). Landingspagina's vangen transactionele zoekintentie af (mensen die klaar zijn om te kopen of contact op te nemen). In een goede SEO silo leiden je blogs de bezoeker uiteindelijk naar je landingspagina.",
      },
    ],
    relatedArticles: ['zoekintentie-uitleg', 'seo-silo-wat-is-het'],
  },
  {
    slug: 'hoe-optimaliseer-je-een-pagina',
    title: 'Hoe optimaliseer je een pagina voor Google?',
    cluster: 'Content SEO',
    metaDescription:
      'De ultieme on-page SEO checklist. Van title tags en meta descriptions tot H-structuren en interne links.',
    content: [
      { type: 'heading', content: 'On-Page SEO Checklist' },
      {
        type: 'paragraph',
        content:
          "Zorg dat je focus zoekwoord in de URL, de H1 titel, de eerste alinea en de meta title staat. Gebruik synoniemen en gerelateerde termen (LSI keywords) in je H2 en H3 tussenkopjes. Vergeet niet te linken naar andere relevante pagina's op je eigen site.",
      },
    ],
    relatedArticles: ['hoe-schrijf-je-seo-teksten', 'technische-seo-uitleg'],
  },

  // --- CLUSTER 5: Local SEO ---
  {
    slug: 'local-seo-uitleg',
    title: 'Wat is local SEO en hoe word je lokaal vindbaar?',
    cluster: 'Local SEO',
    metaDescription:
      'Voor veel mkb-bedrijven en adviseurs is lokale vindbaarheid cruciaal. Leer hoe Local SEO werkt en hoe je de lokale markt domineert.',
    content: [
      { type: 'heading', content: 'Domineer jouw regio in Google' },
      {
        type: 'paragraph',
        content:
          'Als je een financieel adviseur in Alkmaar bent, heb je er weinig aan als iemand uit Maastricht jouw website vindt. Je wilt gevonden worden door mensen in jouw directe omgeving. Dat is precies waar Local SEO (Lokale Zoekmachine Optimalisatie) om draait.',
      },
      { type: 'heading3', content: 'De Local Pack (De Map)' },
      {
        type: 'paragraph',
        content:
          'Wanneer iemand zoekt naar een lokale dienst ("accountant nabij", "hypotheekadviseur amsterdam"), toont Google bijna altijd een kaartje met drie lokale bedrijven. Dit noemen we de "Local Pack". Een plek in deze top 3 is goud waard, omdat het direct boven de normale organische zoekresultaten staat.',
      },
      { type: 'heading3', content: 'De 3 pijlers van Local SEO' },
      {
        type: 'list',
        items: [
          'Google Bedrijfsprofiel (Google My Business): Dit is je belangrijkste asset. Een volledig ingevuld, geoptimaliseerd profiel is een absolute vereiste.',
          'NAP-consistentie: NAP staat voor Name, Address, Phone number. Google checkt of jouw bedrijfsgegevens overal op het internet (KVK, Telefoongids, brancheverenigingen) exact hetzelfde zijn. Inconsistentie schaadt het vertrouwen.',
          'Lokale Reviews: Reviews op je Google profiel zijn een enorme ranking factor voor lokale SEO. Zowel de kwantiteit, de score, als de tekst in de reviews tellen mee.',
        ],
      },
      {
        type: 'callout',
        content:
          'Lokale SEO is vaak de snelste manier om ROI te halen uit zoekmachine optimalisatie, omdat de concurrentie lokaal veel lager is dan landelijk.',
      },
      { type: 'heading3', content: "Lokale Landingspagina's" },
      {
        type: 'paragraph',
        content:
          'Naast de map, wil je ook in de normale resultaten staan. Dit doe je door specifieke landingspagina\'s te maken voor de steden waar je actief bent (bijv. "Financieel Adviseur Haarlem"). Zorg wel dat deze pagina\'s unieke, lokaal relevante content bevatten, anders ziet Google het als spam.',
      },
    ],
    relatedArticles: [
      'hoe-werkt-google-business',
      'wat-is-apple-business-connect',
      'lokaal-vindbaar-worden',
    ],
  },
  {
    slug: 'hoe-werkt-google-business',
    title: 'Hoe werkt Google Business?',
    cluster: 'Local SEO',
    metaDescription:
      'Je Google Bedrijfsprofiel is je digitale etalage. Hoe claim je het, optimaliseer je het en gebruik je het om lokaal te ranken?',
    content: [
      { type: 'heading', content: 'Je belangrijkste lokale asset' },
      {
        type: 'paragraph',
        content:
          'Een Google Bedrijfsprofiel is gratis en essentieel. Zorg dat je categorieën kloppen, voeg regelmatig nieuwe foto\'s toe, reageer op alle reviews (ook de negatieve) en gebruik de "Updates" functie om nieuws te delen.',
      },
    ],
    relatedArticles: ['local-seo-uitleg', 'lokaal-vindbaar-worden'],
  },
  {
    slug: 'wat-is-apple-business-connect',
    title: 'Wat is Apple Business Connect en waarom is het belangrijk?',
    cluster: 'Local SEO',
    metaDescription:
      'Vergeet Apple Maps niet! Miljoenen iPhone gebruikers zoeken lokaal via Apple. Ontdek hoe Apple Business Connect werkt.',
    content: [
      { type: 'heading', content: 'De vergeten zoekmachine' },
      {
        type: 'paragraph',
        content:
          'Iedereen focust op Google Maps, maar miljoenen iPhone-gebruikers gebruiken standaard Apple Maps. Met Apple Business Connect kun je jouw bedrijfsprofiel in het Apple ecosysteem claimen en optimaliseren, een enorme kans die veel concurrenten laten liggen.',
      },
    ],
    relatedArticles: ['local-seo-uitleg', 'hoe-werkt-google-business'],
  },
  {
    slug: 'lokaal-vindbaar-worden',
    title: 'Hoe word je lokaal vindbaar in jouw regio?',
    cluster: 'Local SEO',
    metaDescription:
      'Een concreet stappenplan om de lokale zoekresultaten in jouw stad of regio te domineren.',
    content: [
      { type: 'heading', content: 'Lokale dominantie in 3 stappen' },
      {
        type: 'paragraph',
        content:
          '1. Claim en optimaliseer je Google en Apple profielen. 2. Zorg voor vermeldingen in lokale bedrijvengidsen met consistente gegevens. 3. Vraag actief om reviews van tevreden klanten uit de regio.',
      },
    ],
    relatedArticles: ['local-seo-uitleg', 'hoe-werkt-google-business'],
  },

  // --- CLUSTER 6: Authority & Backlinks ---
  {
    slug: 'wat-zijn-backlinks',
    title: 'Wat zijn backlinks?',
    cluster: 'Authority & Backlinks',
    metaDescription:
      'Backlinks zijn de stemmen van het internet. Wat zijn het precies en waarom hecht Google er zoveel waarde aan?',
    content: [
      { type: 'heading', content: 'Digitale aanbevelingen' },
      {
        type: 'paragraph',
        content:
          'Een backlink is simpelweg een link van een andere website naar jouw website. Google ziet elke link als een "stem" of aanbeveling. Hoe meer kwalitatieve stemmen je hebt, hoe hoger je autoriteit en hoe beter je rankt.',
      },
    ],
    relatedArticles: ['hoe-bouw-je-autoriteit-op', 'goede-vs-slechte-links'],
  },
  {
    slug: 'hoe-bouw-je-autoriteit-op',
    title: 'Hoe bouw je autoriteit op?',
    cluster: 'Authority & Backlinks',
    metaDescription:
      'Linkbuilding is moeilijk, maar cruciaal. Ontdek veilige en effectieve strategieën om de autoriteit van je website te verhogen.',
    content: [
      { type: 'heading', content: 'Verdien links, koop ze niet' },
      {
        type: 'paragraph',
        content:
          'De beste manier om autoriteit op te bouwen is door content te creëren die zó goed, uniek of data-gedreven is, dat andere websites er vanzelf naar willen linken (Digital PR). Daarnaast kun je gastblogs schrijven voor relevante websites in jouw branche.',
      },
    ],
    relatedArticles: ['wat-zijn-backlinks', 'goede-vs-slechte-links'],
  },
  {
    slug: 'goede-vs-slechte-links',
    title: 'Wat is het verschil tussen goede en slechte links?',
    cluster: 'Authority & Backlinks',
    metaDescription:
      'Niet elke backlink is goed voor je SEO. Sommige links kunnen je zelfs een penalty opleveren. Leer het verschil.',
    content: [
      { type: 'heading', content: 'Kwaliteit boven kwantiteit' },
      {
        type: 'paragraph',
        content:
          'Een goede link komt van een relevante, betrouwbare website in jouw branche en is op een natuurlijke manier in de tekst verwerkt. Een slechte link komt van een spammy startpagina, een ongerelateerde buitenlandse site, of is overduidelijk ingekocht.',
      },
    ],
    relatedArticles: ['wat-zijn-backlinks', 'hoe-bouw-je-autoriteit-op'],
  },

  // --- CLUSTER 7: Programmatic SEO ---
  {
    slug: 'wat-is-programmatic-seo',
    title: 'Wat is programmatic SEO?',
    cluster: 'Programmatic SEO',
    metaDescription:
      "Programmatic SEO is de techniek om op grote schaal landingspagina's te genereren op basis van data. Ontdek hoe het werkt.",
    content: [
      { type: 'heading', content: 'SEO op grote schaal' },
      {
        type: 'paragraph',
        content:
          'Programmatic SEO is het gebruik van code en databases om honderden of duizenden geoptimaliseerde pagina\'s te genereren. Denk aan Tripadvisor ("hotels in [stad]") of vacaturesites. Het is ideaal voor bedrijven die dezelfde dienst in veel verschillende regio\'s aanbieden.',
      },
    ],
    relatedArticles: ['hoe-schaal-je-paginas', 'voorkom-duplicate-content'],
  },
  {
    slug: 'hoe-schaal-je-paginas',
    title: 'Hoe schaal je naar 100+ pagina’s?',
    cluster: 'Programmatic SEO',
    metaDescription:
      "Hoe creëer je honderden lokale landingspagina's zonder handmatig alles te typen? De basis van schaalbare SEO.",
    content: [
      { type: 'heading', content: 'De database-gedreven aanpak' },
      {
        type: 'paragraph',
        content:
          'Je bouwt één perfect template en koppelt dit aan een database (bijv. een spreadsheet met steden en lokale data). Het systeem genereert vervolgens voor elke rij in de database een unieke pagina.',
      },
    ],
    relatedArticles: ['wat-is-programmatic-seo', 'voorkom-duplicate-content'],
  },
  {
    slug: 'voorkom-duplicate-content',
    title: 'Hoe voorkom je duplicate content?',
    cluster: 'Programmatic SEO',
    metaDescription:
      "Het grootste risico van programmatic SEO is duplicate content. Hoe zorg je dat Google je gegenereerde pagina's niet als spam ziet?",
    content: [
      { type: 'heading', content: 'Unieke waarde toevoegen' },
      {
        type: 'paragraph',
        content:
          "Als je 100 stadspagina's maakt waarbij alleen de naam van de stad verandert, straft Google je af. Je moet unieke lokale data toevoegen: lokale reviews, specifieke projecten in die stad, of dynamisch gegenereerde teksten via AI.",
      },
    ],
    relatedArticles: ['wat-is-programmatic-seo', 'wat-zijn-canonicals'],
  },

  // --- CLUSTER 8: AI & Entity SEO ---
  {
    slug: 'wat-is-entity-seo',
    title: 'Wat is entity SEO?',
    cluster: 'AI & Entity SEO',
    metaDescription:
      'Google denkt niet meer in zoekwoorden, maar in entiteiten (dingen, personen, concepten). Wat is Entity SEO en hoe pas je het toe?',
    content: [
      { type: 'heading', content: 'Van "strings" naar "things"' },
      {
        type: 'paragraph',
        content:
          "Een entiteit is een uniek, goed gedefinieerd concept (een persoon, plaats, organisatie of idee). Google's Knowledge Graph verbindt deze entiteiten. Door je content te structureren rondom entiteiten in plaats van losse zoekwoorden, begrijpt Google de context van je website veel beter.",
      },
    ],
    relatedArticles: ['wat-is-structured-data', 'hoe-google-websites-begrijpt'],
  },
  {
    slug: 'seo-voor-ai-tools',
    title: 'Hoe werkt SEO voor AI tools zoals ChatGPT?',
    cluster: 'AI & Entity SEO',
    metaDescription:
      'Mensen zoeken steeds vaker via ChatGPT of Perplexity. Hoe zorg je dat jouw bedrijf als antwoord wordt gegeven door AI?',
    content: [
      { type: 'heading', content: 'Optimaliseren voor de LLM' },
      {
        type: 'paragraph',
        content:
          'AI-modellen trainen op betrouwbare, gestructureerde data. Om door AI genoemd te worden, moet je autoriteit extreem hoog zijn, je merknaam vaak genoemd worden in combinatie met je expertise op andere sites, en je eigen content moet feitelijk en helder gestructureerd zijn.',
      },
    ],
    relatedArticles: ['wat-is-entity-seo', 'wat-is-structured-data'],
  },
  {
    slug: 'wat-is-structured-data',
    title: 'Wat is structured data?',
    cluster: 'AI & Entity SEO',
    metaDescription:
      'Structured data (Schema markup) is de taal die zoekmachines direct begrijpen. Hoe gebruik je het om Rich Snippets te krijgen?',
    content: [
      { type: 'heading', content: 'Code die Google direct begrijpt' },
      {
        type: 'paragraph',
        content:
          'Structured data is een stukje code (meestal JSON-LD) dat je aan je website toevoegt. Het vertelt Google expliciet: "Dit is een review met 5 sterren", of "Dit is een FAQ". Dit vergroot de kans op opvallende resultaten (Rich Snippets) in Google.',
      },
    ],
    relatedArticles: ['wat-is-entity-seo', 'technische-seo-uitleg'],
  },

  // --- CLUSTER 9: UX & Conversie ---
  {
    slug: 'wat-is-ux-en-waarom-beinvloedt-het-seo',
    title: 'Wat is UX en waarom beïnvloedt het SEO?',
    cluster: 'UX & Conversie',
    metaDescription:
      'User Experience (UX) en SEO zijn onlosmakelijk met elkaar verbonden. Waarom een goede ranking nutteloos is zonder goede UX.',
    content: [
      { type: 'heading', content: 'Google meet tevredenheid' },
      {
        type: 'paragraph',
        content:
          'Als jouw site op nummer 1 staat, maar iedereen klikt binnen 3 seconden weer terug naar Google (pogo-sticking), weet Google dat jouw site geen goed antwoord was. Je zakt direct. Een goede UX houdt bezoekers vast en stuurt positieve signalen naar Google.',
      },
    ],
    relatedArticles: [
      'hoe-verhoog-je-conversie',
      'waarom-verlaten-bezoekers-website',
    ],
  },
  {
    slug: 'hoe-verhoog-je-conversie',
    title: 'Hoe verhoog je conversie op je website?',
    cluster: 'UX & Conversie',
    metaDescription:
      'Verkeer is leuk, maar leads zijn beter. Praktische tips om meer aanvragen uit je bestaande website bezoekers te halen.',
    content: [
      { type: 'heading', content: 'Van bezoeker naar klant' },
      {
        type: 'paragraph',
        content:
          "Zorg voor duidelijke Call-to-Actions (CTA's), bouw vertrouwen op met reviews en cases, maak formulieren zo kort mogelijk, en zorg dat de bezoeker op elke pagina precies weet wat de logische volgende stap is.",
      },
    ],
    relatedArticles: [
      'wat-is-ux-en-waarom-beinvloedt-het-seo',
      'blogs-vs-landingspaginas',
    ],
  },
  {
    slug: 'waarom-verlaten-bezoekers-website',
    title: 'Waarom verlaten bezoekers je website?',
    cluster: 'UX & Conversie',
    metaDescription:
      'Een hoge bounce rate? Ontdek de meest voorkomende redenen waarom bezoekers je website direct weer verlaten.',
    content: [
      { type: 'heading', content: 'De conversie-killers' },
      {
        type: 'paragraph',
        content:
          'De top 3 redenen voor een hoge bounce rate: 1. De pagina laadt te traag. 2. De content sluit niet aan bij de zoekintentie (ze zochten iets anders). 3. De website is onoverzichtelijk of onleesbaar op mobiel.',
      },
    ],
    relatedArticles: [
      'wat-is-ux-en-waarom-beinvloedt-het-seo',
      'wat-zijn-core-web-vitals',
    ],
  },

  // --- CLUSTER: B2B Groei & Klantacquisitie ---
  {
    slug: 'klanten-werven-financieel-adviseur',
    title: 'Klanten werven als financieel adviseur: voorbij de via-via',
    cluster: 'B2B Groei & Klantacquisitie',
    metaDescription:
      'Als financieel adviseur ben je vaak afhankelijk van doorverwijzingen. Ontdek hoe je structureel nieuwe klanten aantrekt buiten je bestaande netwerk, met een bewezen en data-gedreven aanpak.',
    content: [
      { type: 'heading', content: 'Het via-via-plafond: herkenbaar?' },
      {
        type: 'paragraph',
        content:
          'Je bent goed in wat je doet. Je klanten zijn tevreden, ze verwijzen je door, en zo groeit je praktijk. Tot het stopt. Want via-via heeft een natuurlijk plafond. Je bestaande netwerk is eindig. En zodra de doorverwijzingen opdrogen, merk je dat er geen systeem onder zit. Geen voorspelbare stroom van nieuwe klanten. Alleen hoop.',
      },
      {
        type: 'callout',
        content:
          'Via-via is geen strategie. Het is een bijproduct van goed werk, maar het schaalt niet.',
      },
      {
        type: 'heading3',
        content: 'Waarom traditionele marketing niet werkt voor adviseurs',
      },
      {
        type: 'paragraph',
        content:
          'Veel financieel adviseurs hebben al eens geïnvesteerd in marketing. Een website laten bouwen, misschien wat Google Ads gedraaid, of een social media bureau ingehuurd. Het resultaat? Vaak teleurstellend. Niet omdat de kanalen niet werken, maar omdat de aanpak verkeerd was.',
      },
      {
        type: 'list',
        items: [
          'De website was een digitaal visitekaartje. Mooi, maar zonder strategie om bezoekers aan te trekken.',
          'Google Ads werden ingezet zonder te weten welke zoekwoorden daadwerkelijk klanten opleveren.',
          'Social media kostte veel tijd maar leverde geen meetbare leads op.',
          'Het bureau begreep de financiële sector niet en leverde generieke oplossingen.',
        ],
      },
      {
        type: 'heading3',
        content: 'De visserij-metafoor: waar laat jij je net zakken?',
      },
      {
        type: 'paragraph',
        content:
          "Stel je voor dat je een visser bent. Je hebt een net (je website) en aas (je marketing). De meeste bureaus verkopen je een groter net, met meer pagina's en mooier design. Maar niemand vraagt: weet je eigenlijk wáár de vis zwemt? Een gigantisch sleepnet op een lege oceaan vangt niets. Een klein net op de juiste plek vangt structureel.",
      },
      {
        type: 'paragraph',
        content:
          'Dit is precies wat data-gedreven klantacquisitie oplost. In plaats van te gokken op welke zoekwoorden werken, test je het eerst. Met gerichte advertenties verzamel je 90 dagen lang harde data over welke zoekopdrachten daadwerkelijk leiden tot aanvragen. Pas dán bouw je je online aanwezigheid uit op die bewezen plekken.',
      },
      { type: 'heading3', content: 'Wat werkt wél voor financieel adviseurs?' },
      {
        type: 'list',
        items: [
          'Een SEO-silo die specifiek is opgebouwd rond de vragen die jouw ideale klant stelt (niet generieke financiële termen).',
          'Content die vertrouwen opbouwt: geen verkooppraatjes, maar antwoorden op echte vragen over hypotheken, verzekeringen of pensioen.',
          'Lokale vindbaarheid: als iemand zoekt op "financieel adviseur Alkmaar", moet jij bovenaan staan.',
          'Een website die niet alleen mooi is, maar gebouwd is als een conversiemachine met duidelijke vervolgstappen.',
        ],
      },
      {
        type: 'callout',
        content:
          'De vraag is niet óf je online zichtbaar moet zijn. De vraag is: bouw je op bewijs, of op aannames?',
      },
      { type: 'heading3', content: 'De eerste stap' },
      {
        type: 'paragraph',
        content:
          'Begin niet met een nieuw website-ontwerp of een duur SEO-pakket. Begin met data. Welke zoekwoorden gebruikt jouw ideale klant? Welke daarvan leiden tot echte aanvragen? Dat is het fundament waarop je alles bouwt. Zonder dat fundament bouw je een huis op drijfzand.',
      },
    ],
    relatedArticles: [
      'online-zichtbaarheid-financiele-dienstverlening',
      'data-gedreven-marketing-mkb',
      'website-als-vangnet',
    ],
  },
  {
    slug: 'online-zichtbaarheid-financiele-dienstverlening',
    title:
      'Online zichtbaarheid voor financiële dienstverleners: waar begin je?',
    cluster: 'B2B Groei & Klantacquisitie',
    metaDescription:
      'Als financieel dienstverlener wil je online gevonden worden, maar waar begin je? Of het nu gaat om SEO, ads of social media: dit artikel geeft je een helder stappenplan zonder jargon.',
    content: [
      { type: 'heading', content: 'Door de bomen het bos niet meer zien' },
      {
        type: 'paragraph',
        content:
          'SEO, SEA, social media, content marketing, e-mail marketing, Google Business, Apple Business Connect, lokale SEO... Als ondernemer in de financiële dienstverlening word je overspoeld met opties. Iedereen roept dat hun kanaal "het belangrijkste" is. Het resultaat? Verlamming. Je doet óf niks, óf een beetje van alles, en geen van beide werkt.',
      },
      {
        type: 'callout',
        content:
          'Het probleem is niet dat er te weinig opties zijn. Het probleem is dat niemand je vertelt waar je moet beginnen op basis van jouw specifieke situatie.',
      },
      { type: 'heading3', content: 'Stap 1: Begrijp waar je nu staat' },
      {
        type: 'paragraph',
        content:
          "Voordat je ook maar één euro uitgeeft aan marketing, moet je weten waar je staat. Hoeveel bezoekers krijgt je website per maand? Waar komen ze vandaan? Welke pagina's bekijken ze? En de belangrijkste vraag: hoeveel van die bezoekers nemen daadwerkelijk contact op?",
      },
      {
        type: 'paragraph',
        content:
          'De meeste financieel adviseurs die we spreken, hebben geen idee. Ze hebben een website die 3 jaar geleden is gebouwd, krijgen misschien 50 bezoekers per maand, en hebben geen inzicht in wat die bezoekers doen. Dat is geen verwijt, het is simpelweg nooit uitgelegd.',
      },
      { type: 'heading3', content: 'Stap 2: Kies één kanaal en doe het goed' },
      {
        type: 'paragraph',
        content:
          'De grootste fout die kleine bedrijven maken is overal tegelijk aanwezig willen zijn. LinkedIn, Instagram, Facebook, een blog, een podcast, YouTube... Met 1-5 medewerkers is dat onmogelijk. Kies het kanaal waar jouw doelgroep het meest actief zoekt naar jouw diensten.',
      },
      {
        type: 'paragraph',
        content:
          'Voor financiële dienstverleners is dat bijna altijd Google. Waarom? Omdat mensen die actief zoeken op "hypotheekadviseur in [stad]" of "wat kost een financieel plan" al koopintentie hebben. Ze zoeken een oplossing. Op social media scroll je langs mensen die niet actief op zoek zijn, en dat is een heel ander spel.',
      },
      { type: 'heading3', content: 'Stap 3: Bouw een fundament dat schaalt' },
      {
        type: 'list',
        items: [
          'Google Business Profiel: Claim en optimaliseer je profiel. Dit is de snelste manier om lokaal gevonden te worden.',
          'Website-fundament: Zorg dat je website technisch in orde is (snel, mobiel-vriendelijk, SSL) en dat elke pagina een duidelijk doel heeft.',
          'Content op basis van data: Schrijf niet wat jij interessant vindt, maar wat jouw doelgroep daadwerkelijk zoekt. Gebruik tools of, nog beter, echte advertentiedata om dit te bepalen.',
          'Meetbaarheid: Installeer Google Analytics en Search Console. Zonder data vlieg je blind.',
        ],
      },
      { type: 'heading3', content: 'Het kreeftnetje vs. het sleepnet' },
      {
        type: 'paragraph',
        content:
          "Een standaard website met 5-10 pagina's is als een kreeftnetje. Er is een kleine kans dat er een mooie vis in zwemt, maar je bent afhankelijk van geluk. Een website met een doordachte SEO-silo van 100+ pagina's is als een professioneel sleepnet. Je dekt een veel groter gebied af en vangt structureel.",
      },
      {
        type: 'paragraph',
        content:
          'Het verschil? Het sleepnet is gebouwd op basis van data. Elke pagina beantwoordt een specifieke vraag die jouw doelgroep stelt. Samen vormen ze een netwerk dat Google laat zien dat jij dé autoriteit bent in jouw vakgebied.',
      },
      {
        type: 'callout',
        content:
          "Begin klein, maar begin slim. Eén goed geoptimaliseerde pagina die aansluit bij een bewezen zoekvraag is meer waard dan 20 pagina's op gevoel.",
      },
    ],
    relatedArticles: [
      'klanten-werven-financieel-adviseur',
      'local-seo-uitleg',
      'hoe-werkt-google-business',
    ],
  },
  {
    slug: 'data-gedreven-marketing-mkb',
    title: 'Waarom data-gedreven marketing werkt voor kleine B2B-bedrijven',
    cluster: 'B2B Groei & Klantacquisitie',
    metaDescription:
      'Kleine B2B-bedrijven hebben geen budget om te gokken. Ontdek waarom een data-gedreven aanpak, waarbij je eerst test en dan bouwt, de slimste investering is.',
    content: [
      {
        type: 'heading',
        content: 'Je hebt geen marketingbudget om te verspillen',
      },
      {
        type: 'paragraph',
        content:
          'Als je een bedrijf runt met 1 tot 5 medewerkers, is elke euro die je uitgeeft aan marketing een bewuste keuze. Je kunt het je niet veroorloven om €5.000 per maand in een SEO-bureau te steken en dan na 6 maanden te horen: "Het duurt nog even." Je wilt zekerheid. Of in ieder geval: onderbouwde verwachtingen.',
      },
      {
        type: 'callout',
        content:
          'Het verschil tussen gokken en investeren? Data. Zonder data is marketing een loterij. Met data is het een systeem.',
      },
      { type: 'heading3', content: 'Wat is data-gedreven marketing precies?' },
      {
        type: 'paragraph',
        content:
          'Data-gedreven marketing betekent dat je beslissingen neemt op basis van meetbare feiten, niet op basis van onderbuikgevoel of wat een bureau "aanraadt". In de praktijk werkt het zo:',
      },
      {
        type: 'list',
        items: [
          'Fase 1, Testen: Je draait 90 dagen gerichte Google Ads op verschillende zoekwoorden in jouw markt. Niet om direct klanten te werven, maar om te meten welke zoekwoorden daadwerkelijk leiden tot aanvragen.',
          'Fase 2, Analyseren: Na 90 dagen heb je harde data. Je weet precies welke zoektermen converteren, wat een lead kost, en waar de kansen liggen.',
          'Fase 3, Bouwen: Op basis van die data bouw je je SEO-strategie. Elke pagina, elk artikel en elke optimalisatie is gebaseerd op bewezen resultaten.',
        ],
      },
      {
        type: 'heading3',
        content: 'Waarom dit beter werkt dan de traditionele aanpak',
      },
      {
        type: 'paragraph',
        content:
          'De traditionele aanpak van de meeste marketingbureaus is: keyword research doen (op basis van zoekvolume), content schrijven, en hopen dat het werkt. Het probleem? Zoekvolume zegt niets over conversie. Een zoekwoord met 1.000 zoekopdrachten per maand kan 0 klanten opleveren, terwijl een zoekwoord met 50 zoekopdrachten per maand je beste bron van leads kan zijn.',
      },
      {
        type: 'paragraph',
        content:
          'Door eerst te testen met advertenties, weet je vóórdat je investeert in SEO precies waar de vis zit. Je bouwt je sleepnet niet op een willekeurige plek in de oceaan, maar precies daar waar de vis bewezen toehapt.',
      },
      {
        type: 'heading3',
        content: 'De sonar-visser: een metafoor die het verschil uitlegt',
      },
      {
        type: 'paragraph',
        content: 'Stel je vier vissers voor op dezelfde oceaan:',
      },
      {
        type: 'list',
        items: [
          'De hengelaar: Geen website of een verouderde. Klanten komen alleen via netwerk. Vangt af en toe iets dat toevallig langszwemt.',
          "De kreeftvanger: Een website van 10-15 pagina's. Klein netje, op één plek, hoopt op een mooie vangst.",
          'De optimist met sleepnet: Een uitgebreide site gebouwd door een bureau op basis van zoekvolume. Groot net, maar mogelijk op de verkeerde plek.',
          'De visser met sonar: Eerst peilen waar de vis toehapt (met advertentiedata), pas dán het sleepnet uitzetten. Vangt structureel.',
        ],
      },
      {
        type: 'callout',
        content: 'Welke visser ben jij? En welke wil je zijn?',
      },
      { type: 'heading3', content: 'Wat levert het concreet op?' },
      {
        type: 'paragraph',
        content:
          'Bedrijven die data-gedreven werken, zien gemiddeld 3x meer rendement op hun marketinginvestering. Niet omdat ze meer uitgeven, maar omdat ze slimmer uitgeven. Elke euro gaat naar een bewezen kanaal, een bewezen zoekwoord, een bewezen strategie.',
      },
      {
        type: 'paragraph',
        content:
          'Voor een klein B2B-bedrijf betekent dit: voorspelbare groei. Niet afhankelijk van geluk, niet afhankelijk van via-via, maar een systeem dat maand na maand nieuwe klanten aantrekt.',
      },
    ],
    relatedArticles: [
      'klanten-werven-financieel-adviseur',
      'website-als-vangnet',
      'seo-vs-advertenties',
    ],
  },
  {
    slug: 'website-als-vangnet',
    title:
      'Je website als vangnet: waarom een visitekaartje-site niet genoeg is',
    cluster: 'B2B Groei & Klantacquisitie',
    metaDescription:
      'Een mooie website is niet genoeg. Ontdek waarom je website moet functioneren als een vangnet dat actief klanten binnenhaalt, en hoe je dat bereikt.',
    content: [
      {
        type: 'heading',
        content: 'Je website is geen brochure. Het is een vangnet.',
      },
      {
        type: 'paragraph',
        content:
          'De meeste zakelijke websites zijn digitale visitekaartjes. Een homepage, een "over ons" pagina, een dienstenpagina en een contactformulier. Mooi ontworpen, maar strategisch nutteloos. Want een visitekaartje geef je aan iemand die je al kent. Het trekt geen nieuwe klanten aan.',
      },
      {
        type: 'callout',
        content:
          'Een website die geen nieuwe klanten aantrekt, is een kostenpost. Een website die dat wél doet, is je beste medewerker. Hij is 24/7 actief en nooit ziek.',
      },
      {
        type: 'heading3',
        content: 'Het verschil tussen een visitekaartje en een vangnet',
      },
      {
        type: 'paragraph',
        content:
          'Een visitekaartje-website wacht tot iemand je URL intypt. Een vangnet-website trekt actief bezoekers aan via zoekmachines. Het verschil zit in drie dingen:',
      },
      {
        type: 'list',
        items: [
          "Omvang: Een visitekaartje heeft 5-10 pagina's. Een vangnet heeft 50-200+ pagina's, elk gericht op een specifieke zoekvraag.",
          "Structuur: Een visitekaartje is plat. Een vangnet is georganiseerd in thematische clusters (SEO-silo's) die Google laat zien dat je een autoriteit bent.",
          'Intentie: Een visitekaartje vertelt wat je doet. Een vangnet beantwoordt de vragen die je ideale klant stelt en leidt ze naar een contactmoment.',
        ],
      },
      { type: 'heading3', content: 'Het kreeftnetje vs. het sleepnet' },
      {
        type: 'paragraph',
        content:
          "Denk aan je website als een visnet. Een standaard website met een handvol pagina's is als een kreeftnetje. Je gooit het in het water en hoopt dat er toevallig een mooie vis in zwemt. De kans is klein, en je hebt geen controle over het resultaat.",
      },
      {
        type: 'paragraph',
        content:
          'Een website met een doordachte SEO-silo is als een professioneel sleepnet. Het dekt een enorm gebied af. Elke pagina is een extra stuk net dat een specifieke zoekvraag opvangt. Samen vormen ze een systeem dat structureel vis vangt, dag in dag uit.',
      },
      { type: 'heading3', content: 'Hoe maak je van je website een vangnet?' },
      {
        type: 'list',
        items: [
          'Onderzoek welke vragen je doelgroep stelt: Niet wat jij denkt dat ze zoeken, maar wat ze daadwerkelijk intypen in Google. Gebruik advertentiedata om dit te valideren.',
          "Bouw thematische clusters: Groepeer gerelateerde onderwerpen in silo's. Bijvoorbeeld: alles over hypotheekadvies in één cluster, alles over pensioenplanning in een ander.",
          'Schrijf diepgaande content: Geen oppervlakkige 300-woorden blogjes, maar uitgebreide artikelen die écht antwoord geven op de vraag.',
          "Link intern: Verbind gerelateerde pagina's met elkaar zodat Google de samenhang begrijpt en bezoekers makkelijk doornavigeren.",
          'Optimaliseer voor conversie: Elke pagina moet een duidelijke volgende stap hebben. Een contactformulier, een telefoonnummer, een kennismakingsgesprek.',
        ],
      },
      {
        type: 'heading3',
        content: 'De investering die zichzelf terugverdient',
      },
      {
        type: 'paragraph',
        content:
          'Een vangnet-website kost meer om op te bouwen dan een visitekaartje. Maar het verschil in rendement is enorm. Een visitekaartje kost je €2.000-€5.000 en levert misschien een handvol contactmomenten per jaar op. Een vangnet-website is een investering die maand na maand meer oplevert naarmate Google je autoriteit erkent.',
      },
      {
        type: 'callout',
        content:
          'De vraag is niet: wat kost een goede website? De vraag is: wat kost het je om onzichtbaar te blijven?',
      },
      {
        type: 'paragraph',
        content:
          'Begin met het fundament. Onderzoek waar je doelgroep naar zoekt, valideer het met data, en bouw dan pagina voor pagina je vangnet uit. Niet alles tegelijk, maar wel met een plan.',
      },
    ],
    relatedArticles: [
      'klanten-werven-financieel-adviseur',
      'online-zichtbaarheid-financiele-dienstverlening',
      'seo-silo-wat-is-het',
    ],
  },
]

export const getArticleBySlug = (slug: string): SEOArticle | undefined => {
  return articles.find((article) => article.slug === slug)
}

export const getArticlesByCluster = (cluster: string): SEOArticle[] => {
  return articles.filter((article) => article.cluster === cluster)
}

export const getAllClusters = (): string[] => {
  return Array.from(new Set(articles.map((article) => article.cluster)))
}
